import { gql } from "@apollo/client";
import { useRouter } from "next/router";
import { FC, useMemo, useState } from "react";

import useUser from "@src/features/app/hooks/useUser";
import SelectUsersDialog, {
  USER_SELECTION_FILTERS,
} from "@src/features/contentLibrary/assignmentDialogs/SelectUsersDialog";
import UserSelectionSetPreview from "@src/features/contentLibrary/assignmentDialogs/UserSelectionSetPreview";
import useUserSelectionSetInputState from "@src/features/contentLibrary/assignmentDialogs/hooks/useUserSelectionSetInputState";
import DotsIcon from "@src/lib/icons/18px/dots";
import PlusIcon from "@src/lib/icons/18px/plus";
import UserMinusOutlineIcon from "@src/lib/icons/18px/user-minus-outline";
import { useToast } from "@src/lib/hooks/useToast";
import Avatar from "@src/lib/ui/Avatar";
import { Button } from "@src/lib/ui/Button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@src/lib/ui/Dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@src/lib/ui/DropdownMenu";
import { Input } from "@src/lib/ui/Input";
import { Switch } from "@src/lib/ui/Switch";
import Text from "@src/lib/ui/Text";
import { GraphqlOperations, UserSelectionInput } from "@src/types.generated";

import { ChatDetailFragment } from "./ChatDetailPage.generated";
import DeleteChatDialog from "./DeleteChatDialog";
import {
  useGroupChatOwnerUsersQuery,
  useUpdateChatSettingsMutation,
} from "./GroupChatDialog.generated";
import RemoveSelfAsOwnerDialog from "./RemoveSelfAsOwnerDialog";
import { getMemberRoleSubtitle } from "./utils";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  chat?: ChatDetailFragment;
  selection?: UserSelectionInput;
  loading?: boolean;
  onDeleted?: () => void;
  onCreate?: (args: {
    title?: string;
    membersCanEditTitle: boolean;
    membersCanEditMembers: boolean;
    ownerUserIds: number[];
  }) => void;
};

const PEOPLE_ONLY_HIDE_FILTERS = USER_SELECTION_FILTERS.filter(
  (filter) => filter !== "people",
);

const sameIdSet = (a: number[], b: number[]): boolean =>
  a.length === b.length && [...a].sort().join() === [...b].sort().join();

const GroupChatDialog: FC<Props> = ({
  open,
  onOpenChange,
  chat,
  selection,
  loading = false,
  onCreate,
  onDeleted,
}) => {
  const router = useRouter();
  const { user } = useUser();
  const { addToast, addErrorToast } = useToast();

  const canEditTitle = !chat || chat.viewerIsOwner || chat.membersCanEditTitle;
  const canEditMembers =
    !!chat && (chat.viewerIsOwner || chat.membersCanEditMembers);
  const isOwner = !chat || chat.viewerIsOwner;

  const initialOwnerIds = useMemo(
    () => chat?.owners.map((owner) => owner.id) ?? (user ? [user.id] : []),
    [chat?.owners, user],
  );

  const [title, setTitle] = useState(chat?.title?.en ?? "");
  const [ownerUserIds, setOwnerUserIds] = useState(initialOwnerIds);
  const [membersCanEditTitle, setMembersCanEditTitle] = useState(
    chat?.membersCanEditTitle ?? false,
  );
  const [membersCanEditMembers, setMembersCanEditMembers] = useState(
    chat?.membersCanEditMembers ?? false,
  );
  const [membersDirty, setMembersDirty] = useState(false);
  const [editMembersOpen, setEditMembersOpen] = useState(false);
  const [addOwnerOpen, setAddOwnerOpen] = useState(false);
  const [removeSelfAsOwnerOpen, setRemoveSelfAsOwnerOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const { userSelectionSetInput, editUserSelection } =
    useUserSelectionSetInputState(
      chat
        ? { initialUserSelectionSetFragment: chat.userSelectionSet }
        : { initialUserSelectionInput: selection },
    );

  const chatOwnersById = useMemo(
    () => new Map(chat?.owners.map((owner) => [owner.id, owner])),
    [chat?.owners],
  );
  const newOwnerIds = ownerUserIds.filter((id) => !chatOwnersById.has(id));

  const { data: ownersData, previousData: previousOwnersData } =
    useGroupChatOwnerUsersQuery({
      variables: {
        input: {
          allLocations: false,
          allManagers: false,
          allTrainees: false,
          allAdmins: false,
          userIds: newOwnerIds,
        },
      },
      skip: newOwnerIds.length === 0,
    });
  const newOwnersById = new Map(
    (
      ownersData?.GetUsersForUserSelection ??
      previousOwnersData?.GetUsersForUserSelection ??
      []
    ).map((owner) => [owner.id, owner]),
  );
  const owners = ownerUserIds
    .map((id) => chatOwnersById.get(id) ?? newOwnersById.get(id))
    .filter((owner) => owner != null);

  const [updateChatSettings] = useUpdateChatSettingsMutation({
    refetchQueries: [GraphqlOperations.Query.NewMessagingChats],
  });

  const trimmedTitle = title.trim();
  const titleChanged = trimmedTitle !== (chat?.title?.en ?? "");
  const ownersChanged = !sameIdSet(ownerUserIds, initialOwnerIds);

  const removeOwner = (ownerId: number) =>
    setOwnerUserIds((ids) => ids.filter((id) => id !== ownerId));

  const handleSave = async () => {
    if (!chat) {
      onCreate?.({
        title: trimmedTitle || undefined,
        membersCanEditTitle,
        membersCanEditMembers,
        ownerUserIds,
      });
      return;
    }
    const permissionsChanged =
      membersCanEditTitle !== chat.membersCanEditTitle ||
      membersCanEditMembers !== chat.membersCanEditMembers;
    setSaving(true);
    try {
      const res = await updateChatSettings({
        variables: {
          input: {
            chatId: chat.id,
            ...(titleChanged && { title: { en: trimmedTitle } }),
            ...(isOwner && ownersChanged && { ownerUserIds }),
            ...(isOwner &&
              permissionsChanged && {
                membersCanEditTitle,
                membersCanEditMembers,
              }),
            ...(membersDirty && { userSelectionSet: userSelectionSetInput }),
          },
        },
      });
      if (!res.data?.updateChatSettings.success) {
        addErrorToast({ data: res.data, callsite: "edit_group_chat" });
        return;
      }
      onOpenChange(false);
      if (!res.data.updateChatSettings.chat) {
        addToast({ message: "You left the chat" });
        router.push("/messages");
      } else {
        addToast({ message: "Chat updated" });
      }
    } catch (error) {
      addErrorToast({ error, callsite: "edit_group_chat" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="flex max-h-[90vh] max-w-[560px] flex-col gap-0 p-0">
          <DialogHeader className="items-center border-b border-input px-5 py-3">
            <DialogTitle asChild>
              <Text variant="compact-title">
                {chat ? "Edit Group Chat" : "New Group Chat"}
              </Text>
            </DialogTitle>
          </DialogHeader>
          <div className="flex flex-1 flex-col gap-6 overflow-auto p-5">
            <div className="flex flex-col gap-2">
              <Text variant="compact-title">Custom title</Text>
              <Input
                value={title}
                placeholder="Title"
                disabled={!canEditTitle}
                onChange={(e) => setTitle(e.target.value)}
              />
            </div>
            <div className="flex flex-col gap-2">
              <Text variant="compact-title">Members</Text>
              <UserSelectionSetPreview
                input={userSelectionSetInput}
                chatId={chat?.id}
                showUserTypeOptions
                disabled={!canEditMembers}
                showAdd={false}
                showRemove={false}
                onEdit={chat ? () => setEditMembersOpen(true) : undefined}
                onAdd={chat ? () => setEditMembersOpen(true) : undefined}
              />
            </div>
            {isOwner && (
              <div className="flex flex-col gap-4">
                <Text variant="compact-title">Owners</Text>
                <div className="flex flex-col gap-3">
                  {owners.map((owner) => (
                    <div key={owner.id} className="flex items-center gap-2.5">
                      <Avatar person={owner} size={40} />
                      <div className="flex min-w-0 flex-1 flex-col gap-1">
                        <Text variant="compact-title" ellipsis>
                          {owner.name}
                        </Text>
                        <Text
                          variant="fine-print-description"
                          className="text-gray-8"
                          ellipsis
                        >
                          {getMemberRoleSubtitle(owner)}
                        </Text>
                      </div>
                      <DropdownMenu modal={false}>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            aria-label={`Owner options for ${owner.name}`}
                          >
                            <DotsIcon />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            className="text-red-9 focus:text-red-9"
                            disabled={ownerUserIds.length <= 1}
                            onSelect={() =>
                              owner.id === user?.id
                                ? setRemoveSelfAsOwnerOpen(true)
                                : removeOwner(owner.id)
                            }
                          >
                            <UserMinusOutlineIcon className="size-4" />
                            Remove owner
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  ))}
                </div>
                <div className="flex">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setAddOwnerOpen(true)}
                  >
                    <PlusIcon className="size-4" />
                    Add an owner
                  </Button>
                </div>
              </div>
            )}
            {isOwner && (
              <div className="flex flex-col gap-4">
                <div className="flex flex-col gap-2">
                  <Text variant="compact-title">Permissions</Text>
                  <Text
                    variant="fine-print-description"
                    className="text-gray-9"
                  >
                    Turning off these permissions means only chat owners can
                    take these actions
                  </Text>
                </div>
                <PermissionToggle
                  label="Members can edit chat title"
                  checked={membersCanEditTitle}
                  onCheckedChange={setMembersCanEditTitle}
                />
                <PermissionToggle
                  label="Members can add and remove members"
                  checked={membersCanEditMembers}
                  onCheckedChange={setMembersCanEditMembers}
                />
              </div>
            )}
          </div>
          <DialogFooter className="border-t border-input p-3 sm:justify-between">
            {chat && isOwner ? (
              <Button
                variant="outline"
                className="text-status-error-text hover:bg-status-error-bg hover:text-status-error-text"
                onClick={() => setDeleteOpen(true)}
                disabled={saving || loading}
              >
                Delete
              </Button>
            ) : (
              <div />
            )}
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={saving || loading}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                loading={saving || loading}
                disabled={
                  ownerUserIds.length === 0 ||
                  userSelectionSetInput.userSelections.length === 0
                }
              >
                Save
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
        {removeSelfAsOwnerOpen && (
          <RemoveSelfAsOwnerDialog
            open={removeSelfAsOwnerOpen}
            onOpenChange={setRemoveSelfAsOwnerOpen}
            onConfirm={() => {
              if (user) {
                removeOwner(user.id);
              }
              setRemoveSelfAsOwnerOpen(false);
            }}
          />
        )}
        {deleteOpen && chat && (
          <DeleteChatDialog
            open={deleteOpen}
            onOpenChange={setDeleteOpen}
            chatId={chat.id}
            onDeleted={() => {
              onOpenChange(false);
              onDeleted?.();
            }}
          />
        )}
      </Dialog>
      {editMembersOpen && (
        <SelectUsersDialog
          open={editMembersOpen}
          onOpenChange={setEditMembersOpen}
          title="Edit members"
          pillColor="purple"
          requireSelection
          showUserTypeOptions
          defaultFilter="people"
          input={userSelectionSetInput.userSelections[0]}
          onSelect={(selected) => {
            editUserSelection(0, selected);
            setMembersDirty(true);
            setEditMembersOpen(false);
          }}
        />
      )}
      {addOwnerOpen && (
        <SelectUsersDialog
          open={addOwnerOpen}
          onOpenChange={setAddOwnerOpen}
          title="Add an owner"
          pillColor="purple"
          requireSelection
          hideFilters={PEOPLE_ONLY_HIDE_FILTERS}
          onSelect={(selected) => {
            const addedIds = selected.userIds ?? [];
            setOwnerUserIds((ids) =>
              Array.from(new Set([...ids, ...addedIds])),
            );
            setAddOwnerOpen(false);
          }}
        />
      )}
    </>
  );
};

const PermissionToggle: FC<{
  label: string;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
}> = ({ label, checked, onCheckedChange }) => (
  <label className="flex items-center justify-between gap-3">
    <Text variant="compact-title">{label}</Text>
    <Switch checked={checked} onCheckedChange={onCheckedChange} />
  </label>
);

gql`
  query GroupChatOwnerUsers($input: UserSelectionInput!) {
    GetUsersForUserSelection(input: $input) {
      id
      name
      firstName
      deactivatedAt
      userType
      profileImage {
        ...AvatarProfileImage
      }
      roles {
        id
        name
      }
      locations {
        id
        name
      }
    }
  }

  mutation UpdateChatSettings($input: UpdateChatSettingsInput!) {
    updateChatSettings(input: $input) {
      success
      error {
        code
      }
      chat {
        ...ChatDetail
      }
    }
  }
`;

export default GroupChatDialog;
